import { type SyntheticEvent, useEffect, useRef } from 'react';

import { Link } from '@tanstack/react-router';

import { AuthPasswordField } from '@nesso/ui/primitives/auth-password-field';
import { Button } from '@nesso/ui/primitives/button';
import { FieldError } from '@nesso/ui/primitives/field';
import { InputField } from '@nesso/ui/primitives/input-field';

import { useSignInWithPassword } from '../lib/use-sign-in-with-password';
import { SignInWithPasswordCooldownCountdown } from './sign-in-with-password/sign-in-with-password-cooldown-countdown';
import { SignInWithPasswordInvalidCredentialsAlert } from './sign-in-with-password/sign-in-with-password-invalid-credentials-alert';

type Props = {
    role: string;
    returnUrl?: string;
    onCooldownChange?: (retryAfterSeconds: number | null) => void;
    onLocked?: (locked: { email: string } | null) => void;
};

export function SignInWithPassword({ role, returnUrl, onCooldownChange, onLocked }: Props) {
    const {
        email,
        password,
        errors,
        invalidCredentials,
        isSubmitting,
        cooldown,
        isCoolingDown,
        endCooldown,
        locked,
        setEmail,
        setPassword,
        handleEmailBlur,
        handlePasswordBlur,
        handleSubmit,
    } = useSignInWithPassword(role, returnUrl);

    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    const onSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
        const fieldToFocus = await handleSubmit(event);
        if (fieldToFocus === 'email') emailRef.current?.focus();
        else if (fieldToFocus === 'password') passwordRef.current?.focus();
    };

    useEffect(() => {
        invalidCredentials && emailRef.current?.focus();
    }, [invalidCredentials]);

    useEffect(() => {
        onCooldownChange?.(cooldown?.seconds ?? null);
    }, [cooldown, onCooldownChange]);

    useEffect(() => {
        onLocked?.(locked);
    }, [locked, onLocked]);

    const disabled = isSubmitting || isCoolingDown;

    return (
        <form className="flex flex-col gap-6" noValidate onSubmit={onSubmit}>
            {invalidCredentials ? <SignInWithPasswordInvalidCredentialsAlert /> : null}

            <InputField
                ref={emailRef}
                id="sign-in-email"
                type="email"
                label="Email"
                autoComplete="email"
                value={email}
                disabled={disabled}
                error={errors.email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={handleEmailBlur}
            />

            <AuthPasswordField
                ref={passwordRef}
                id="sign-in-password"
                value={password}
                error={errors.password}
                disabled={disabled}
                autoComplete="current-password"
                labelAction={
                    <Link to="/forgot-password" className="text-sm">
                        Forgot your password?
                    </Link>
                }
                onChange={setPassword}
                onBlur={handlePasswordBlur}
            />

            {errors.form ? <FieldError>{errors.form}</FieldError> : null}

            <Button type="submit" size="lg" className="w-full" disabled={disabled}>
                Log In
            </Button>

            {cooldown && (
                <SignInWithPasswordCooldownCountdown
                    deadlineAt={cooldown.until}
                    onFinish={endCooldown}
                />
            )}

            <p className="text-center text-sm text-muted-foreground">
                New to Nesso?{' '}
                <Link to="/signup" className="underline underline-offset-4">
                    Create an Account
                </Link>
            </p>
        </form>
    );
}
